import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as ReactDOMServer from 'react-dom/server';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as TSwiper, SwiperOptions } from 'swiper/types';

import { defaultTheme } from '@treezor/design-system';

import { FrontPaymentCard } from '@/features/cards/components/CardUI/FrontPaymentCard';

import { useProfileContext } from '@/features/context/ProfileContext';
import { selectProfileCardsByUserId } from '@/store/selectors';
import { setCurrentCardId } from '@/store/actions/profile.actions';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { useAppSelector } from '@/hooks/redux-helpers';

const SwiperStyled = styled(Swiper)<SwiperOptions>`
  max-width: 22rem;
  position: initial;

  .swiper-pagination {
    position: relative;
    max-width: inherit;
    bottom: unset;
    margin-top: 1.5rem;
    z-index: 2;
  }
`;

const PaginationStyled = styled.span`
  width: 1.8rem;
  height: 1.8rem;
  text-align: center;
  line-height: 1.8rem;
  color: ${defaultTheme.palette.primary};
  font-weight: ${defaultTheme.typography.fontWeightMedium};
  opacity: 1;
  background-color: transparent;
  transition: background-color 300ms ease;
  :hover {
    background-color: ${defaultTheme.theme.backgroundColor.secondary};
  }

  &.swiper-pagination-bullet-active {
    background-color: ${defaultTheme.theme.backgroundColor.interactive};
  }
`;

export const CardsSlider = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user: { userId },
  } = useProfileContext();
  const [swiperInstance, setSwiperInstance] = useState<TSwiper>();
  const { cardId } = useParams();

  const paymentCards = useAppSelector(
    (state) => selectProfileCardsByUserId(state, userId)?.state?.map((cards) => cards.state?.card),
    {
      devModeChecks: { stabilityCheck: 'never' },
    },
  );

  const cardIndex = paymentCards?.map((paymentCard) => paymentCard?.cardId).indexOf(Number(cardId));

  useEffect(() => {
    if (cardIndex && swiperInstance) {
      swiperInstance.slideTo(cardIndex, 0, false);
    }
  }, [swiperInstance, cardIndex]);

  const setCardId = useCallback(
    (nextSlide: number) => {
      const paymentCard = paymentCards?.[nextSlide];
      if (paymentCard) {
        dispatch(setCurrentCardId(userId, paymentCard.cardId));
        navigate(DashboardHelpers.generateProfilePath(paymentCard.userId, { cardId: paymentCard.cardId }));
      }
    },
    [dispatch, navigate, paymentCards, userId],
  );

  const pagination = {
    clickable: true,
    renderBullet: (index, className) =>
      ReactDOMServer.renderToStaticMarkup(
        <PaginationStyled className={className} data-testid={`card-pagination-${index}`}>
          {index + 1}
        </PaginationStyled>,
      ),
  };

  return (
    <SwiperStyled
      spaceBetween={10}
      pagination={pagination}
      modules={[Pagination]}
      className="mySwiper"
      onSlideChange={(e) => setCardId(e.activeIndex)}
      onSwiper={setSwiperInstance}
    >
      {paymentCards?.map(
        (paymentCard) =>
          paymentCard && (
            <SwiperSlide key={paymentCard.cardId}>
              <FrontPaymentCard paymentCard={paymentCard} />
            </SwiperSlide>
          ),
      )}
    </SwiperStyled>
  );
};

export default CardsSlider;
